import api, { get } from './apiService';

/**
 * Test Service - handles all test-related API calls
 */
class TestService {
  /**
   * Get all test cases with optional filtering
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response
   */
  async getTests(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filters to query parameters
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== '') {
          params.append(key, filters[key]);
        }
      });
      
      const queryString = params.toString();
      const url = queryString ? `/api/tests?${queryString}` : '/api/tests';
      
      const response = await api.authGet(url);
      return response;
    } catch (error) {
      console.error('Error fetching tests:', error);
      throw error;
    }
  }

  /**
   * Get test case by ID
   * @param {string} testId - Test case ID
   * @returns {Promise} API response
   */
  async getTest(testId) {
    try {
      const response = await api.authGet(`/api/tests/${testId}`);
      return response;
    } catch (error) {
      console.error('Error fetching test:', error);
      throw error;
    }
  }

  /**
   * Create new test case
   * @param {Object} testData - Test case data
   * @returns {Promise} API response
   */
  async createTest(testData) {
    try {
      const response = await api.authPost('/api/tests', testData);
      return response;
    } catch (error) {
      console.error('Error creating test:', error);
      throw error;
    }
  }

  /**
   * Update test case
   * @param {string} testId - Test case ID
   * @param {Object} testData - Updated test case data
   * @returns {Promise} API response
   */
  async updateTest(testId, testData) {
    try {
      const response = await api.authPut(`/api/tests/${testId}`, testData);
      return response;
    } catch (error) {
      console.error('Error updating test:', error);
      throw error;
    }
  }

  /**
   * Delete test case
   * @param {string} testId - Test case ID
   * @returns {Promise} API response
   */
  async deleteTest(testId) {
    try {
      const response = await api.authDelete(`/api/tests/${testId}`);
      return response;
    } catch (error) {
      console.error('Error deleting test:', error);
      throw error;
    }
  }

  /**
   * Get test case metrics and statistics
   * @returns {Promise} API response
   */
  async getTestMetrics() {
    try {
      const response = await api.authGet('/api/test/metrics');
      return response;
    } catch (error) {
      console.error('Error fetching test metrics:', error);
      throw error;
    }
  }

  /**
   * Get quarterly test growth data for all quarters
   * @param {Object} options - Query options
   * @param {number} options.startYear - Start year (default: 2018)
   * @param {number} options.endYear - End year (default: current year)
   * @returns {Promise} API response
   */
  async getQuarterlyGrowthData(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.startYear) {
        params.append('startYear', options.startYear);
      }
      
      if (options.endYear) {
        params.append('endYear', options.endYear);
      }
      
      const queryString = params.toString();
      const url = queryString ? `/api/test/quarterly-growth?${queryString}` : '/api/test/quarterly-growth';
      
      const response = await api.authGet(url);
      return response;
    } catch (error) {
      console.error('Error fetching quarterly growth data:', error);
      throw error;
    }
  }

  /**
   * Get test growth data for a specific quarter
   * @param {number} year - Year
   * @param {number} quarter - Quarter (1-4)
   * @returns {Promise} API response
   */
  async getQuarterGrowthData(year, quarter) {
    try {
      const response = await api.authGet(`/api/test/quarterly-growth/${year}/${quarter}`);
      return response;
    } catch (error) {
      console.error('Error fetching quarter growth data:', error);
      throw error;
    }
  }

  /**
   * Get test growth trends for the last 12 months
   * @returns {Promise} API response
   */
  async getGrowthTrends() {
    try {
      const response = await api.authGet('/api/test/growth-trends');
      return response;
    } catch (error) {
      console.error('Error fetching growth trends:', error);
      throw error;
    }
  }

  /**
   * Get test cases grouped by test suite
   * @returns {Promise} API response
   */
  async getTestsBySuite() {
    try {
      const response = await api.authGet('/api/test/by-suite');
      return response;
    } catch (error) {
      console.error('Error fetching tests by suite:', error);
      throw error;
    }
  }

  /**
   * Get automation summary with test suites as columns
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response
   */
  async getAutomationSummary(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== '') {
          params.append(key, filters[key]);
        }
      });
      
      const queryString = params.toString();
      const url = queryString ? `/api/test/automation-summary?${queryString}` : '/api/test/automation-summary';
      
      const response = await api.authGet(url);
      return response;
    } catch (error) {
      console.error('Error fetching automation summary:', error);
      throw error;
    }
  }

  /**
   * Get test executions for a release
   * @param {string} releaseVersion - Release version
   * @returns {Promise} API response
   */
  async getTestExecutions(releaseVersion) {
    try {
      const response = await get(`/api/test/executions?releaseVersion=${releaseVersion}`);
      return response;
    } catch (error) {
      console.error('Error fetching test executions:', error);
      throw error;
    }
  }

  /**
   * Update test execution outcome
   * @param {string} testId - Test ID
   * @param {Object} updateData - Update data including outcome and releaseVersion
   * @returns {Promise} API response
   */
  async updateTestExecution(testId, updateData) {
    try {
      const response = await api.authPut(`/api/test/executions/${testId}`, updateData);
      return response;
    } catch (error) {
      console.error('Error updating test execution:', error);
      throw error;
    }
  }

  /**
   * Get release bugs
   * @param {string} releaseVersion - Release version
   * @returns {Promise} API response
   */
  async getReleaseBugs(releaseVersion) {
    try {
      const response = await get(`/api/test/release-bugs?releaseVersion=${releaseVersion}`);
      return response;
    } catch (error) {
      console.error('Error fetching release bugs:', error);
      throw error;
    }
  }

  /**
   * Get release traceability data
   * @param {string} releaseVersion - Release version (optional)
   * @param {string} team - Team name (optional)
   * @returns {Promise} API response
   */
  async getReleaseTraceability(releaseVersion, team) {
    try {
      let url = '/api/fix-versions/traceability?';
      if (releaseVersion) {
        url += `releaseVersion=${encodeURIComponent(releaseVersion)}`;
      }
      if (team) {
        if (releaseVersion) url += '&';
        url += `team=${encodeURIComponent(team)}`;
      }
      const response = await api.authGet(url);
      return response;
    } catch (error) {
      console.error('Error fetching release traceability:', error);
      throw error;
    }
  }

  /**
   * Fetch tests from ADO test plan
   * @param {string} testPlanId - Test plan ID
   * @param {string} releaseVersion - Release version
   * @returns {Promise} API response
   */
  async fetchAdoTests(testPlanId, releaseVersion) {
    try {
      const response = await api.authGet(`/api/test/ado-tests/${testPlanId}?releaseVersion=${releaseVersion}`);
      return response;
    } catch (error) {
      console.error('Error fetching ADO tests:', error);
      throw error;
    }
  }

  /**
   * Generate QA report DOCX
   * @param {string} releaseVersion - Release version
   * @returns {Promise} Blob response
   */
  async generateQaReportDocx(releaseVersion) {
    try {
      const response = await api.authGet(`/api/test/docx/qa-report/${releaseVersion}`, null, 'blob');
      return response;
    } catch (error) {
      console.error('Error generating QA report DOCX:', error);
      throw error;
    }
  }

  /**
   * Test CORS configuration
   * @returns {Promise} API response
   */
  async testCors() {
    try {
      const response = await api.authGet('/api/test/cors');
      return response;
    } catch (error) {
      console.error('Error testing CORS:', error);
      throw error;
    }
  }
}

// Export singleton instance
const testService = new TestService();
export default testService;

// Named exports for backward compatibility
export const getReleaseBugs = (releaseVersion) => testService.getReleaseBugs(releaseVersion);
export const getTestExecutions = (releaseVersion) => testService.getTestExecutions(releaseVersion);
export const getReleaseTraceability = (releaseVersion, team) => testService.getReleaseTraceability(releaseVersion, team);
export const fetchAdoTests = (testPlanId, releaseVersion) => testService.fetchAdoTests(testPlanId, releaseVersion);
export const getQuarterlyGrowthData = (options) => testService.getQuarterlyGrowthData(options);
